// useRagHistory — load v_facility_rag_history for a single facility.
// View is security_invoker so RLS resolves through the underlying tables
// (facilities for live audit_log rows, rag_status_backfill for the
// historical KPI-document import). AMs see only their own facilities;
// superuser/COO see everything; other TCS employees can read for context.
//
// Returns ordered DESC by set_at so callers can slice [0..5] for the
// "last 5 entries" timeline and feed the full set into a "View all"
// expansion.
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import type { FacilityRagHistoryEntry } from '../../../shared/types';

export function useRagHistory(facility_id: string | null | undefined) {
  const [entries, setEntries] = useState<FacilityRagHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!facility_id) {
        setEntries([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from('v_facility_rag_history')
        .select(
          'facility_id, rag_status_old, rag_status_new, rag_category, rag_reason, set_by_employee_id, set_by_name, set_at, source',
        )
        .eq('facility_id', facility_id)
        .order('set_at', { ascending: false });
      if (cancelled) return;
      if (err) {
        console.error('[use-rag-history] load failed', err);
        setError(err.message);
        setEntries([]);
      } else {
        setEntries((data ?? []) as FacilityRagHistoryEntry[]);
      }
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [facility_id, reloadKey]);

  return { entries, loading, error, refresh };
}
